import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import api from '../api';
import {
  PageHeader,
  Card,
  Button,
  Table,
  LoadingSpinner,
  StatsCard,
} from '../components/UI';
import { useAuth } from '../context/AuthContext';
import * as I from 'lucide-react';

/**
 * TdsPage — shows only INVOICE_PAID invoices and allows grouping selected invoices by `tdsRule`
 * and generating TDS batches.
 * 
 * Toolbar added above the table with:
 *  - Search input (left)
 *  - Select All button (right)
 *  - Generate TDS Batches (By Section) button (right)
 *
 * UI styling minimally changed to place toolbar above table while preserving existing styles.
 */

const ConfirmModal = ({ isOpen, onClose, groupsSummary, onConfirm, isPosting }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl">
        <div className="p-4 border-b flex items-center justify-between">
          <h3 className="text-lg font-semibold">Confirm Generate TDS Batches</h3>
          <button className="text-gray-500 hover:text-gray-700" onClick={onClose}>✕</button>
        </div>

        <div className="p-4 max-h-[60vh] overflow-auto">
          <p className="text-sm text-gray-600 mb-3">
            The selected invoices will be grouped by TDS rule and one batch will be created per rule.
          </p>

          {groupsSummary.length === 0 ? (
            <div className="text-sm text-gray-500">No groups to create.</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-500">
                  <th className="py-2">TDS Rule</th>
                  <th className="py-2 text-right">Invoices</th>
                  <th className="py-2 text-right">Total TDS</th>
                </tr>
              </thead>
              <tbody>
                {groupsSummary.map(g => (
                  <tr key={g.tdsRule} className="border-t">
                    <td className="py-2">{g.tdsRule}</td>
                    <td className="py-2 text-right">{g.count}</td>
                    <td className="py-2 text-right font-medium">
                      {new Intl.NumberFormat('en-IN',{style:'currency',currency:'INR',minimumFractionDigits:2}).format(g.totalTdsAmount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="p-4 border-t flex justify-end gap-3">
          <Button variant="secondary" onClick={onClose} disabled={isPosting}>Cancel</Button>
          <Button onClick={onConfirm} disabled={isPosting || groupsSummary.length === 0}>
            {isPosting ? 'Creating...' : 'Confirm & Create Batches'}
          </Button>
        </div>
      </div>
    </div>
  );
};

const TdsPage = () => {
  const { isAuditor } = useAuth(); // AUDITOR is read-only: hide batch-generation actions
  const [invoices, setInvoices] = useState([]);
  const [loadingInvoices, setLoadingInvoices] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [pageSize] = useState(20);
  const [currentPage, setCurrentPage] = useState(0);
  const [vendorMap, setVendorMap] = useState({});

  // selection and generating state
  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState(new Set());
  const [isGenerating, setIsGenerating] = useState(false);

  // confirmation modal state
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [groupsSummary, setGroupsSummary] = useState([]);

  // Helper: fetch invoices — forced to INVOICE_PAID
  const fetchInvoices = useCallback(async () => {
    setLoadingInvoices(true);
    setError('');
    try {
      const params = new URLSearchParams();
      params.append('page', currentPage);
      params.append('size', pageSize);
      params.append('sort', 'invoiceDate,desc');

      // Always fetch paid invoices
      params.append('status', 'INVOICE_PAID');

      const resp = await api.get(`/invoices?${params.toString()}`);
      const raw = resp.data?.content || resp.data || [];

      const filtered = (Array.isArray(raw) ? raw : []).filter(inv => (inv.status || '').toString().toUpperCase() !== 'REJECTED');

      const mapped = filtered.map(inv => ({
        id: inv.id,
        vendorId: inv.vendorId,
        invoiceNumber: inv.invoiceNumber,
        dateReceived: inv.dateReceived || inv.receivedDateTime || null,
        invoiceDate: inv.invoiceDate || null,
        dueDate: inv.dueDate || null,
        subtotal: inv.subtotal != null ? Number(inv.subtotal) : null,
        tax: inv.tax != null ? Number(inv.tax) : null,
        totalAmount: inv.totalAmount != null ? Number(inv.totalAmount) : null,
        tdsAmount: inv.tdsAmount != null ? Number(inv.tdsAmount) : null,
        invoicePayable: inv.invoicePayable != null ? Number(inv.invoicePayable) : null,
        tdsRule: inv.tdsRule || inv.tdsSection || null,
        tdsPercentage: inv.tdsPercentage != null ? Number(inv.tdsPercentage) : null,
        // Prefer invoicePaidDate (LocalDate); fallback to invoicePaidTime for older data
        invoicePaidDate: inv.invoicePaidDate || inv.invoicePaidTime || null,
        invoicePaymentMethod: inv.invoicePaymentMethod || null,
        invoicePaymentReference: inv.invoicePaymentReference || null,
        status: (inv.status || '').toString().toLowerCase(),
        batchNumber: inv.batchNumber || null,
      }));

      setInvoices(mapped);

      // attempt bulk vendor map
      const vendorIds = mapped.map(m => m.vendorId).filter(Boolean);
      if (vendorIds.length) {
        try {
          const vresp = await api.get('/vendors');
          if (vresp.data?.content && Array.isArray(vresp.data.content)) {
            const vm = vresp.data.content.reduce((acc, v) => {
              if (v.id && v.name) acc[v.id] = v.name;
              return acc;
            }, {});
            setVendorMap(prev => ({ ...prev, ...vm }));
          }
        } catch (e) {
          console.warn('Vendor bulk fetch failed', e);
        }
      }

    } catch (err) {
      console.error('Fetch invoices error', err);
      setError('Failed to fetch invoices.');
      setInvoices([]);
    } finally {
      setLoadingInvoices(false);
    }
  }, [currentPage, pageSize]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  // helpers
  const computeTdsForInvoice = (inv) => {
    if (inv.tdsAmount != null) return Number(inv.tdsAmount);
    const perc = inv.tdsPercentage != null ? Number(inv.tdsPercentage) : 0;
    const base = inv.invoicePayable != null ? Number(inv.invoicePayable) : Number(inv.totalAmount || 0);
    return parseFloat(((base * perc) / 100).toFixed(2));
  };

  const toggleSelectInvoice = (id) => {
    setSelectedInvoiceIds(prev => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id); else s.add(id);
      return s;
    });
  };

  const selectAllInvoices = (checked) => {
    if (checked) {
      const ids = invoices.map(inv => inv.id);
      setSelectedInvoiceIds(new Set(ids));
    } else {
      setSelectedInvoiceIds(new Set());
    }
  };

  const isAllSelected = invoices.length > 0 && selectedInvoiceIds.size === invoices.length;

  // Prepare groups and open confirmation modal
  const handlePrepareGenerate = () => {
    if (selectedInvoiceIds.size === 0) {
      toast.info('Please select at least one invoice to create TDS deduction batches.');
      return;
    }

    const selected = invoices.filter(inv => selectedInvoiceIds.has(inv.id));

    const groups = selected.reduce((acc, inv) => {
      const rule = inv.tdsRule || 'UNKNOWN';
      if (!acc[rule]) acc[rule] = [];
      acc[rule].push(inv);
      return acc;
    }, {});

    const summary = Object.entries(groups).map(([tdsRule, list]) => {
      const totalTdsAmount = list.reduce((s, i) => s + computeTdsForInvoice(i), 0);
      return {
        tdsRule,
        count: list.length,
        totalTdsAmount: parseFloat(totalTdsAmount.toFixed(2)),
        invoiceIds: list.map(i => i.id),
      };
    });

    setGroupsSummary(summary);
    setConfirmOpen(true);
  };

  // Confirm & POST grouped batches
  const handleConfirmGenerate = async () => {
    if (groupsSummary.length === 0) {
      toast.info('No invoices available for TDS batch creation.');
      return;
    }

    setIsGenerating(true);
    try {
      const payloadBatches = groupsSummary.map(g => ({
        tdsRule: g.tdsRule,
        invoiceIds: g.invoiceIds,
        totalTdsAmount: g.totalTdsAmount
      }));

      const resp = await api.post('/tds/generate-batches', { batches: payloadBatches });

      const created = resp?.data?.createdBatches || resp?.data || [];
      const createdCount = Array.isArray(created) ? created.length : 0;

      toast.success(`Created ${createdCount} TDS deduction batch(es) for tax filing.`);
      if (createdCount > 0) {
        const lines = (Array.isArray(created) ? created : []).map(b => {
          const label = b.batchNumber || b.id || b.tdsRule || 'Batch';
          const amt = b.totalTdsAmount != null ? Number(b.totalTdsAmount) : (b.totalTds || 0);
          return `${label} — ${b.tdsRule}: ${new Intl.NumberFormat('en-IN',{style:'currency',currency:'INR'}).format(amt)}`;
        });
        if (lines.length) toast.info(lines.join('\n'), { autoClose: 8000 });
      }

      setSelectedInvoiceIds(new Set());
      setConfirmOpen(false);
      setGroupsSummary([]);
      await fetchInvoices();
    } catch (err) {
      console.error('Generate batches error', err);
      const msg = err?.response?.data?.message || 'Failed to generate batches. See console for details.';
      toast.error(msg);
    } finally {
      setIsGenerating(false);
    }
  };

  // UI helpers
  const formatCurrency = (amount) => {
    if (amount == null || Number.isNaN(Number(amount))) return '-';
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2 }).format(Number(amount));
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const d = new Date(dateString);
    if (Number.isNaN(d.getTime())) return 'Invalid Date';
    return d.toLocaleDateString('en-IN');
  };

  // Table building (search still available)
  const filtered = invoices.filter(inv => {
    if (!searchTerm) return true;
    const q = searchTerm.toLowerCase();
    const vendorName = vendorMap[inv.vendorId] || '';
    return (inv.invoiceNumber && inv.invoiceNumber.toLowerCase().includes(q)) || (vendorName && vendorName.toLowerCase().includes(q));
  });

  const tableHeaders = [
    <input type="checkbox" checked={isAllSelected} onChange={(e) => selectAllInvoices(e.target.checked)} />,
    'Invoice #',
    'Vendor',
    'Invoice Date',
    'Subtotal',
    'Tax',
    'Total',
    'TDS (amt / % / rule)',
    'Invoice Paid',
  ];

  const tableData = filtered.map(inv => {
    const vendorName = vendorMap[inv.vendorId] || `Vendor ${inv.vendorId || ''}`;
    const tdsAmt = inv.tdsAmount != null ? inv.tdsAmount : computeTdsForInvoice(inv);
    return [
      <input type="checkbox" checked={selectedInvoiceIds.has(inv.id)} onChange={() => toggleSelectInvoice(inv.id)} />,
      inv.invoiceNumber || 'N/A',
      <div className="max-w-[180px] text-xs leading-snug break-words whitespace-normal">{vendorName}</div>,
      inv.invoiceDate ? formatDate(inv.invoiceDate) : 'N/A',
      inv.subtotal != null ? formatCurrency(inv.subtotal) : 'N/A',
      inv.tax != null ? formatCurrency(inv.tax) : 'N/A',
      inv.totalAmount != null ? formatCurrency(inv.totalAmount) : 'N/A',
      <div>
        <span className="font-semibold text-blue-600">{formatCurrency(tdsAmt)}</span>
        {inv.tdsPercentage != null ? ` (${inv.tdsPercentage}%)` : ''}
        <div className="text-xs text-gray-500">{inv.tdsRule || 'No rule'}</div>
      </div>,
      inv.invoicePayable != null ? <span className="font-semibold text-blue-600">{formatCurrency(inv.invoicePayable)}</span> : 'N/A',
    ];
  });

  // Stats
  const stats = {
    totalInvoices: invoices.length,
    selectedCount: selectedInvoiceIds.size,
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4 bg-gray-50 min-h-screen">
      <PageHeader
        title="TDS: Paid Invoices — Generate Batches"
        subtitle=" Select invoices and create TDS batches grouped by TDS rule."
        actions={[
          <Button key="refresh" variant="outline" onClick={fetchInvoices}>
            <I.RefreshCw className={`w-4 h-4 mr-2 ${loadingInvoices ? 'animate-spin' : ''}`} />
            Refresh
          </Button>,
        ]}
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <StatsCard title="Paid Invoices (view)" value={`${stats.totalInvoices}`} icon={I.FileText} />
        <StatsCard title="Selected" value={`${stats.selectedCount}`} icon={I.CheckSquare} />
      </div>

      {/* ---------- Toolbar (matches your screenshot placement) ---------- */}
      <Card className="shadow-sm">
        <div className="flex items-center justify-between gap-4 p-4 border-b border-gray-200">
          {/* left: search */}
          <div className="flex-1 min-w-0 relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
              <I.Search className="w-4 h-4 text-gray-400" />
            </span>
            <input
              type="text"
              placeholder="Search by invoice number or vendor..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 h-10 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            />
          </div>

          {/* right: Select All + Generate */}
          {!isAuditor && (
          <div className="flex items-center gap-3 flex-shrink-0">
            <Button
              variant="secondary"
              onClick={() => selectAllInvoices(!isAllSelected)}
              title={isAllSelected ? 'Deselect all invoices' : 'Select all invoices'}
            >
              {isAllSelected ? 'Deselect All' : 'Select All'}
            </Button>

            <Button
              variant="primary"
              onClick={handlePrepareGenerate}
              disabled={selectedInvoiceIds.size === 0}
              title={selectedInvoiceIds.size === 0 ? 'Select invoices to generate batches' : 'Generate TDS batches for selected invoices'}
            >
              Generate TDS Batches (By Section)
            </Button>
          </div>
          )}
        </div>

        <div className="overflow-x-auto">
          {loadingInvoices && invoices.length === 0 ? (
            <div className="flex items-center justify-center py-16">
              <LoadingSpinner size="lg" />
              <span className="ml-3 text-lg text-gray-500">Loading invoices...</span>
            </div>
          ) : error ? (
            <div className="text-center py-16 px-4">
              <div className="text-red-500 mb-4">
                <I.AlertTriangle className="w-12 h-12 mx-auto mb-3" />
              </div>
              <h3 className="text-lg font-semibold text-gray-700 mb-2">TDS Management System Error</h3>
              <p className="text-gray-500 text-sm mb-4">{error}</p>
              <Button onClick={fetchInvoices} variant="outline">Try Again</Button>
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16 px-4">
              <I.SearchX className="w-12 h-12 mx-auto mb-3 text-gray-400" />
              <h3 className="text-lg font-semibold text-gray-700 mb-2">No Paid Invoices Found</h3>
              <p className="text-gray-500 text-sm">
                {searchTerm ? "No invoices match your current search." : "No paid invoices available."}
              </p>
            </div>
          ) : (
            <Table headers={tableHeaders} data={tableData} loading={loadingInvoices} />
          )}
        </div>
      </Card>

      {/* Confirmation modal */}
      <ConfirmModal
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        groupsSummary={groupsSummary}
        onConfirm={handleConfirmGenerate}
        isPosting={isGenerating}
      />

    </div>
  );
};

export default TdsPage;
